import React, { useState, useEffect, useMemo } from 'react';
import { trans } from '../utils';

/**
 * Centered, Scrollable Error Modal for Upload & Validation Errors
 * Features:
 * - Centered screen layout with backdrop blur
 * - Error count summary badge
 * - Filter/Search bar for large error sets
 * - "Copy All" and "Download Log" actions
 * - Individual error row cards with quick-copy
 * - Fully localized and RTL-compatible
 */
const ErrorModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [rawErrors, setRawErrors] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [copiedAll, setCopiedAll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    const isRtl = localStorage.getItem('lang') === 'ar';

    useEffect(() => {
        const handleShowErrorModal = (event) => {
            const detail = event.detail || {};
            const parsedErrors = parseErrorInput(detail.errors || detail.error || detail);
            setRawErrors(parsedErrors);
            setTitle(detail.title || trans('Errors Detected'));
            setSearchQuery('');
            setCopiedAll(false);
            setCopiedIndex(null);
            setIsOpen(true);
        };

        window.addEventListener('aeonShowErrorModal', handleShowErrorModal);
        return () => {
            window.removeEventListener('aeonShowErrorModal', handleShowErrorModal);
        };
    }, []);

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    const filteredErrors = useMemo(() => {
        if (!searchQuery.trim()) return rawErrors;
        const q = searchQuery.toLowerCase();
        return rawErrors.filter((err) => {
            const text = typeof err === 'string' ? err : `${err.row || ''} ${err.field || ''} ${err.message || ''}`;
            return text.toLowerCase().includes(q);
        });
    }, [rawErrors, searchQuery]);

    const handleCopyAll = () => {
        const fullText = rawErrors.map((err, i) => {
            if (typeof err === 'string') return `${i + 1}. ${err}`;
            return `${i + 1}. ${err.row ? `[Row ${err.row}] ` : ''}${err.field ? `${err.field}: ` : ''}${err.message || JSON.stringify(err)}`;
        }).join('\n');

        navigator.clipboard.writeText(fullText).then(() => {
            setCopiedAll(true);
            setTimeout(() => setCopiedAll(false), 2000);
        });
    };

    const handleCopySingle = (err, index) => {
        const text = typeof err === 'string' ? err : `${err.row ? `[Row ${err.row}] ` : ''}${err.field ? `${err.field}: ` : ''}${err.message || ''}`;
        navigator.clipboard.writeText(text).then(() => {
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 1500);
        });
    };

    const handleDownloadLog = () => {
        const fullText = `=== ${title} ===\nDate: ${new Date().toLocaleString()}\nTotal Errors: ${rawErrors.length}\n\n` +
            rawErrors.map((err, i) => {
                if (typeof err === 'string') return `[#${i + 1}] ${err}`;
                return `[#${i + 1}] ${err.row ? `Row ${err.row} | ` : ''}${err.field ? `Field: ${err.field} | ` : ''}${err.message || JSON.stringify(err)}`;
            }).join('\n');

        const blob = new Blob([fullText], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `error_report_${Date.now()}.txt`;
        link.click();
        URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    return (
        <div
            className="aeon-error-modal-backdrop"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.65)',
                backdropFilter: 'blur(4px)',
                zIndex: 1090,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px',
                direction: isRtl ? 'rtl' : 'ltr',
                animation: 'aeonFadeIn 0.2s ease-out'
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) setIsOpen(false);
            }}
        >
            <div
                className="aeon-error-modal-card"
                style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    width: '100%',
                    maxWidth: '680px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
                    border: '1px solid rgba(220, 38, 38, 0.2)',
                    overflow: 'hidden',
                    animation: 'aeonSlideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '20px 24px 16px',
                        borderBottom: '1px solid #f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'linear-gradient(180deg, #fef2f2 0%, #ffffff 100%)'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                            style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: '#fee2e2',
                                color: '#dc2626',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '22px',
                                flexShrink: 0
                            }}
                        >
                            <i className="bi bi-exclamation-octagon-fill"></i>
                        </div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <h4 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: '#1e293b' }}>
                                    {title}
                                </h4>
                                <span
                                    style={{
                                        backgroundColor: '#dc2626',
                                        color: '#ffffff',
                                        fontSize: '0.75rem',
                                        fontWeight: '700',
                                        padding: '2px 8px',
                                        borderRadius: '9999px',
                                        letterSpacing: '0.025em'
                                    }}
                                >
                                    {rawErrors.length} {rawErrors.length === 1 ? trans('Error') : trans('Errors')}
                                </span>
                            </div>
                            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                                {trans('Please review the details below and correct the issues before re-submitting.')}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        aria-label="Close"
                        style={{
                            border: 'none',
                            background: '#f1f5f9',
                            color: '#64748b',
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* Toolbar (Search + Copy All + Download) */}
                <div
                    style={{
                        padding: '12px 24px',
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #f1f5f9',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px'
                    }}
                >
                    {rawErrors.length > 3 ? (
                        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '180px' }}>
                            <i
                                className="bi bi-search"
                                style={{
                                    position: 'absolute',
                                    [isRtl ? 'right' : 'left']: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#94a3b8',
                                    fontSize: '13px'
                                }}
                            ></i>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder={trans('Search in errors...')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    [isRtl ? 'paddingRight' : 'paddingLeft']: '30px',
                                    borderRadius: '8px',
                                    fontSize: '0.85rem',
                                    borderColor: '#cbd5e1'
                                }}
                            />
                        </div>
                    ) : (
                        <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>
                            {trans('Error List')}
                        </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                            type="button"
                            onClick={handleCopyAll}
                            className="btn btn-sm btn-light-primary"
                            style={{
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                padding: '5px 12px',
                                borderRadius: '8px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <i className={copiedAll ? 'bi bi-check-lg text-success' : 'bi bi-clipboard'}></i>
                            {copiedAll ? trans('Copied!') : trans('Copy All')}
                        </button>

                        <button
                            type="button"
                            onClick={handleDownloadLog}
                            className="btn btn-sm btn-light-secondary"
                            style={{
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                padding: '5px 12px',
                                borderRadius: '8px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <i className="bi bi-download"></i>
                            {trans('Download Log')}
                        </button>
                    </div>
                </div>

                {/* Scrollable Error Content Area */}
                <div
                    style={{
                        padding: '16px 24px',
                        overflowY: 'auto',
                        maxHeight: '48vh',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                    }}
                >
                    {filteredErrors.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
                            <i className="bi bi-check2-circle fs-1 text-success d-block mb-2"></i>
                            {trans('No errors match your search.')}
                        </div>
                    ) : (
                        filteredErrors.map((err, index) => {
                            const isString = typeof err === 'string';
                            const rowNum = isString ? null : err.row;
                            const fieldName = isString ? null : err.field;
                            const messageText = isString ? err : (err.message || JSON.stringify(err));
                            const isSingleCopied = copiedIndex === index;

                            return (
                                <div
                                    key={index}
                                    style={{
                                        padding: '12px 14px',
                                        backgroundColor: '#fff5f5',
                                        border: '1px solid #fee2e2',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: '12px',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1 }}>
                                        <span
                                            style={{
                                                backgroundColor: '#fca5a5',
                                                color: '#7f1d1d',
                                                fontSize: '0.72rem',
                                                fontWeight: '700',
                                                padding: '2px 6px',
                                                borderRadius: '6px',
                                                marginTop: '2px',
                                                flexShrink: 0
                                            }}
                                        >
                                            #{index + 1}
                                        </span>

                                        <div style={{ fontSize: '0.88rem', color: '#1f2937', wordBreak: 'break-word', lineHeight: '1.5' }}>
                                            {rowNum && (
                                                <span
                                                    style={{
                                                        backgroundColor: '#fef3c7',
                                                        color: '#92400e',
                                                        fontWeight: '600',
                                                        fontSize: '0.75rem',
                                                        padding: '1px 6px',
                                                        borderRadius: '4px',
                                                        [isRtl ? 'marginLeft' : 'marginRight']: '6px'
                                                    }}
                                                >
                                                    {trans('Row')} {rowNum}
                                                </span>
                                            )}
                                            {fieldName && (
                                                <span
                                                    style={{
                                                        backgroundColor: '#e0e7ff',
                                                        color: '#3730a3',
                                                        fontWeight: '600',
                                                        fontSize: '0.75rem',
                                                        padding: '1px 6px',
                                                        borderRadius: '4px',
                                                        [isRtl ? 'marginLeft' : 'marginRight']: '6px'
                                                    }}
                                                >
                                                    {fieldName}
                                                </span>
                                            )}
                                            <span>{messageText}</span>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        title={trans('Copy this error')}
                                        onClick={() => handleCopySingle(err, index)}
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            color: isSingleCopied ? '#16a34a' : '#9ca3af',
                                            cursor: 'pointer',
                                            padding: '4px 6px',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            flexShrink: 0,
                                            transition: 'color 0.15s ease'
                                        }}
                                    >
                                        <i className={isSingleCopied ? 'bi bi-check-lg' : 'bi bi-clipboard'}></i>
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div
                    style={{
                        padding: '14px 24px',
                        borderTop: '1px solid #f1f5f9',
                        backgroundColor: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                        {filteredErrors.length !== rawErrors.length
                            ? `${trans('Showing')} ${filteredErrors.length} ${trans('of')} ${rawErrors.length}`
                            : `${rawErrors.length} ${rawErrors.length === 1 ? trans('issue recorded') : trans('issues recorded')}`}
                    </span>

                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="btn btn-secondary px-4 py-2"
                        style={{
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '0.88rem'
                        }}
                    >
                        {trans('Close')}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes aeonFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes aeonSlideUp {
                    from { opacity: 0; transform: scale(0.95) translateY(10px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
            `}</style>
        </div>
    );
};

/**
 * Universal error parser to convert any backend error response into a clean array of error items
 */
export function parseErrorInput(errOrMsg, fallbackMsg = 'An error occurred') {
    if (!errOrMsg) return [trans(fallbackMsg)];

    // Direct String
    if (typeof errOrMsg === 'string') {
        const lines = errOrMsg.split('\n').map(s => s.trim()).filter(Boolean);
        return lines.length > 0 ? lines : [trans(errOrMsg)];
    }

    // Direct Array
    if (Array.isArray(errOrMsg)) {
        const flat = [];
        errOrMsg.forEach((item) => {
            if (typeof item === 'string') {
                flat.push(item);
            } else if (item && typeof item === 'object') {
                if (item.message) {
                    flat.push({
                        row: item.row || item.line || null,
                        field: item.field || item.attribute || null,
                        message: item.message
                    });
                } else if (item.errors) {
                    const sub = Array.isArray(item.errors) ? item.errors : [item.errors];
                    sub.forEach(msg => {
                        flat.push({
                            row: item.row || item.line || null,
                            field: item.attribute || item.field || null,
                            message: typeof msg === 'string' ? msg : JSON.stringify(msg)
                        });
                    });
                } else {
                    flat.push(JSON.stringify(item));
                }
            }
        });
        return flat.length > 0 ? flat : [trans(fallbackMsg)];
    }

    // Axios Error Handling
    const resData = errOrMsg?.response?.data;
    if (resData) {
        // 1. Laravel Validation Errors: resData.errors
        if (resData.errors) {
            const errs = resData.errors;
            const messages = [];

            if (Array.isArray(errs)) {
                errs.forEach(err => {
                    if (typeof err === 'string') {
                        messages.push(err);
                    } else if (err && typeof err === 'object') {
                        messages.push({
                            row: err.row || err.line || null,
                            field: err.attribute || err.field || null,
                            message: err.message || (Array.isArray(err.errors) ? err.errors.join(', ') : JSON.stringify(err))
                        });
                    }
                });
            } else if (typeof errs === 'object') {
                Object.entries(errs).forEach(([key, fieldErrors]) => {
                    // Try to extract row number if key is like "0.serial_number" or "rows.5.name"
                    let rowNum = null;
                    let cleanField = key;
                    const match = key.match(/(?:rows?\.)?(\d+)\.(.+)/);
                    if (match) {
                        rowNum = parseInt(match[1], 10) + 1; // 1-indexed for user-friendly display
                        cleanField = match[2];
                    }

                    if (Array.isArray(fieldErrors)) {
                        fieldErrors.forEach(msg => {
                            messages.push({
                                row: rowNum,
                                field: cleanField,
                                message: typeof msg === 'string' ? msg : JSON.stringify(msg)
                            });
                        });
                    } else if (typeof fieldErrors === 'string') {
                        messages.push({
                            row: rowNum,
                            field: cleanField,
                            message: fieldErrors
                        });
                    }
                });
            }

            if (messages.length > 0) return messages;
        }

        // 2. resData.message
        if (Array.isArray(resData.message)) {
            return resData.message;
        }
        if (typeof resData.message === 'string' && resData.message.trim()) {
            const lines = resData.message.split('\n').map(s => s.trim()).filter(Boolean);
            return lines.length > 0 ? lines : [resData.message];
        }

        // 3. resData.msg or resData.error
        if (resData.msg) return [resData.msg];
        if (resData.error) {
            return [typeof resData.error === 'string' ? resData.error : JSON.stringify(resData.error)];
        }
    }

    // Generic JS Error object
    if (errOrMsg.message) {
        return [errOrMsg.message];
    }

    return [trans(fallbackMsg)];
}

export default ErrorModal;
